import { useState } from "react";
import { Button, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AddGroupIcon, AddIcon, SavedFilters } from "../../../../../assets/icons";
import { savedFilterToGroup } from "../../../../../common/FiltersFormEngine/mappers/savedFilterToGroup";
import { QueryAction } from "../../../../../common/FiltersFormEngine/queryReducer";
import { GroupFilter } from "../../../../../common/FiltersFormEngine/types";
import { FilterDefinition } from "../../../../../context/FiltersProvider/types";
import { FilterContexType } from "../../../../layout/ViewHeader";
import SavedFiltersMenu from "../../../FiltersMenu/components/SavedFiltersMenu";
import FilterItemRenderer from "../FilterItemRenderer";

type Props = {
	group: GroupFilter;
	depth?: number;
	filterContext?: FilterContexType;
	dispatch: React.Dispatch<QueryAction>;
	readOnly?: boolean;
};

const FilterGroup = ({ group, depth = 0, filterContext, dispatch, readOnly }: Props) => {
	const { t } = useTranslation("common");
	const theme = useTheme();

	const [savedAnchorEl, setSavedAnchorEl] = useState<HTMLElement | null>(null);

	const handleOpenSavedMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
		setSavedAnchorEl(e.currentTarget);
	};

	const handleCloseSavedMenu = () => {
		setSavedAnchorEl(null);
	};

	const handleAddSavedFilter = (filter: FilterDefinition) => {
		dispatch({
			type: "ADD_SAVED_GROUP",
			groupId: group.id,
			group: savedFilterToGroup(filter.filter),
		});
	};

	return (
		<Stack
			p={2}
			width={"100%"}
			spacing={2}
			gap={2}
			sx={{
				borderRadius: 2,
				border: depth > 0 ? `1px solid ${theme.palette.grey[400]}` : "none",
				backgroundColor:
					depth === 1
						? theme.palette.background.elevation0
						: depth === 2
							? theme.palette.background.elevation1
							: theme.palette.background.paper,
			}}
		>
			<Stack gap={2} width={"100%"}>
				{group.rows.map((item, index) => {
					if (!item) return null;

					const canDelete = group.rows.length > 1;

					const isFirst = index === 0;

					return (
						<FilterItemRenderer
							key={item.value.id}
							item={item}
							depth={depth + 1}
							canDelete={canDelete}
							isFirst={isFirst}
							parentId={group.id}
							parentOperator={group.rowOperator}
							filterContext={filterContext}
							dispatch={dispatch}
							readOnly={readOnly}
						/>
					);
				})}
			</Stack>

			{!readOnly && (
				<Stack spacing={2} direction={"row"} justifyContent={"end"}>
					{/* Add Filter */}
					<Button
						size="small"
						startIcon={<AddIcon />}
						onClick={() => {
							dispatch({
								type: "ADD_ROW",
								groupId: group.id,
							});
						}}
					>
						{t("add filter")}
					</Button>

					<Button
						disabled={depth === 2}
						size="small"
						startIcon={<SavedFilters />}
						onClick={handleOpenSavedMenu}
					>
						{t("add saved filter")}
					</Button>

					{/* ADD GROUP */}
					<Button
						size="small"
						disabled={depth === 2}
						startIcon={<AddGroupIcon />}
						onClick={() => {
							dispatch({
								type: "ADD_GROUP",
								groupId: group.id,
							});
						}}
					>
						{t("add group")}
					</Button>
				</Stack>
			)}
			<SavedFiltersMenu
				anchorEl={savedAnchorEl}
				open={Boolean(savedAnchorEl)}
				onClose={handleCloseSavedMenu}
				onSelect={handleAddSavedFilter}
				filterContext={filterContext}
			/>
		</Stack>
	);
};

export default FilterGroup;
